/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2021 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import styled from "styled-components";
import { addMinutes, differenceInMinutes } from "date-fns";

import { useTheme } from "@material-ui/core/styles";
import {
  Grid,
  Typography,
  IconButton,
  Backdrop,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import CarrierClaimSpotLeg from "../components/spot-leg-details/CarrierClaimSpotLeg";
import CarrierSpotLegInfo from "../components/spot-leg-details/CarrierSpotLegInfo";
import NotesPanel from "../components/common/NotesPanel";
import Loader from "../components/Loader";
import AlertsIcon from "../components/common/AlertsIcon";
import StyledTextField from "../components/common/StyledTextField";
import BaseButton from "../components/common/BaseButton";

import types from "../helpers/types";
import { isCarrier } from "../helpers/goTrackUserUtils";
import { utcToZonedDateTime } from "../helpers/dateHelpers";

import { fetchSpotNotes, markSpotNotesRead } from "../slices/spotNotesSlice";
import { clearSpotLeg } from "../slices/spotLegSlice";
import { addSpotLegNote } from "../slices/updateSpotLegSlice";
import ActionsModal from "../components/common/ActionsModal";

const StyledHeader = styled(({ theme, ...props }) => <Grid {...props} />)`
  background-color: ${(props) => props.theme.palette.background.headerBlue};
  padding: 12px 16px;
`;

const StyledGridContainer = styled(Grid)`
  padding: 16px;
`;

function CarrierSpotLegDetails({ handleNotification }) {
  const theme = useTheme();
  const dispatch = useDispatch();

  const {
    notes,
    getRequestStatus: getSpotNotesRequestStatus,
    putRequestStatus: putSpotNotesRequestStatus,
  } = useSelector((state) => state.spotNotes);
  const { addSpotLegNoteRequestStatus } = useSelector(
    (state) => state.updateSpotLeg
  );
  const { goTrackUser } = useSelector((state) => state.goTrackUser);
  const spotLegDetails = useSelector(
    (state) => state.spotLegDetails.spotLeg.spotLeg
  );
  const spotLegClaim = useSelector(
    (state) => state.spotLegDetails.spotLeg.spotLegClaim
  );
  const isSpotLegDetailsFetching = useSelector(
    (state) =>
      state.spotLegDetails.requestStatus === types.requestStatuses.inProgress
  );
  const isMultiLegRoute = useSelector(
    (state) => state.spotLegDetails.spotLeg.multiLegRoute
  );
  const [claimAlreadyProcessedModalOpen, setClaimAlreadyProcessedModalOpen] =
    useState(false);

  const [proposedDepartureDateTime, setProposedDepartureDateTime] =
    useState(null);
  const [proposedArrivalDateTime, setProposedArrivalDateTime] = useState(null);

  const [note, setNote] = useState("");

  const handleCloseButtonClick = () => {
    dispatch(clearSpotLeg());
  };

  useEffect(() => {
    dispatch(fetchSpotNotes(spotLegDetails.spotLegsId, handleNotification));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const unreadNotes = notes
      ?.filter((note) => !note.isRead && note.position === "left")
      .map((note) => note.notesId);

    if (
      spotLegDetails &&
      unreadNotes?.length > 0 &&
      putSpotNotesRequestStatus !== types.requestStatuses.inProgress
    ) {
      dispatch(markSpotNotesRead(spotLegDetails.spotLegsId, unreadNotes));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (proposedDepartureDateTime) {
      const proposedArrivalDateTime = getProposedArrivalDateTime();
      setProposedArrivalDateTime(proposedArrivalDateTime);
    }
  }, [proposedDepartureDateTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const getProposedArrivalDateTime = () => {
    const scheduledDepartureDateTime = utcToZonedDateTime(
      spotLegDetails.scheduledDepartureDateTimeUTC,
      spotLegDetails.movementOriginFacility.timeZoneName
    );
    const scheduledArrivalDateTime = utcToZonedDateTime(
      spotLegDetails.scheduledArrivalDateTimeUTC,
      spotLegDetails.movementDestinationFacility.timeZoneName
    );

    const timeDifference = differenceInMinutes(
      proposedDepartureDateTime,
      scheduledDepartureDateTime
    );

    const proposedArrivalDateTime = addMinutes(
      scheduledArrivalDateTime,
      timeDifference
    );
    return proposedArrivalDateTime;
  };

  const handleNoteTextFieldChange = (event) => {
    setNote(event.target.value);
  };

  const handleSubmittingNote = () => {
    if (note) {
      dispatch(
        addSpotLegNote(
          spotLegDetails.spotLegsId,
          { contents: note, isPrivate: false },
          handleNotification
        )
      );
      setNote("");
    }
  };

  const handleUnableToPerformActionModalClose = () => {
    setClaimAlreadyProcessedModalOpen(false);
  };

  const isSpotNotesFetching =
    types.requestStatuses.inProgress == getSpotNotesRequestStatus;
  const isAddingSpotNote =
    types.requestStatuses.inProgress == addSpotLegNoteRequestStatus;

  return (
    <>
      <ActionsModal
        open={claimAlreadyProcessedModalOpen}
        title={"Unable to Perform Action"}
        message={
          "Sorry, we are unable to cancel this claim as this claim has already been processed."
        }
        handleAffirmative={handleUnableToPerformActionModalClose}
        affirmativeButtonText="Close"
      />
      <StyledHeader
        container
        alignItems="center"
        theme={theme}
        data-testid="carrier-spot-leg-details"
      >
        <Grid item>
          <Typography variant="body1" component="h2">
            <span style={{ fontWeight: "bold" }}>ID:</span>{" "}
            {spotLegDetails.spotLegsId}
            <span style={{ fontWeight: "bold", marginLeft: "10px" }}>
              Route Code:
            </span>{" "}
            {spotLegDetails.routeCode}
            <span style={{ fontWeight: "bold", marginLeft: "10px" }}>Route ID:</span>{" "}
            {spotLegDetails.upsRouteId}
          </Typography>
        </Grid>
        <Grid item style={{ marginLeft: "auto" }}>
          <IconButton size="small" onClick={handleCloseButtonClick}>
            <CloseIcon
              fontSize="small"
              data-testid="spot-leg-details-close-button"
            />
          </IconButton>
        </Grid>
      </StyledHeader>
      <div style={{ contain: "paint", minHeight: "100%" }}>
        <Backdrop
          open={isSpotNotesFetching || isSpotLegDetailsFetching}
          style={{
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: "#fff",
            opacity: 1,
          }}
        >
          <Loader />
        </Backdrop>
        {isCarrier(goTrackUser.roles) && (
          <StyledGridContainer container wrap="wrap">
            <Grid item component="h4" xs={12}>
              Leg Info
            </Grid>
            <CarrierSpotLegInfo
              spotLegDetails={spotLegDetails}
              proposedDepartureDateTime={proposedDepartureDateTime}
              proposedArrivalDateTime={proposedArrivalDateTime}
            />
          </StyledGridContainer>
        )}
        <StyledGridContainer item container wrap="wrap">
          <Grid item component="h4">
            Claim Leg
          </Grid>
          {!isMultiLegRoute && (
            <>
              {proposedDepartureDateTime ||
                spotLegClaim?.proposedPickupTimeUtc ? (
                <Typography
                  style={{
                    paddingLeft: 8,
                    fontSize: 8,
                    color: theme.palette.primary.red,
                    lineHeight: 2.7,
                  }}
                >
                  <AlertsIcon
                    style={{
                      fontSize: 12,
                      marginRight: 4,
                      verticalAlign: "middle",
                    }}
                    color={theme.palette.primary.red}
                  />
                  Requested schedule changes in red.
                </Typography>
              ) : (
                <Typography
                  style={{
                    paddingLeft: 8,
                    fontSize: 8,
                    color: "#495A63",
                    lineHeight: 2.7,
                  }}
                >
                  If you cannot make schedule departure, propose new times
                  below.
                </Typography>
              )}
            </>
          )}
          <CarrierClaimSpotLeg
            handleNotification={handleNotification}
            setProposedDepartureDateTime={setProposedDepartureDateTime}
            setProposedArrivalDateTime={setProposedArrivalDateTime}
            setClaimAlreadyProcessedModalOpen={
              setClaimAlreadyProcessedModalOpen
            }
          />
        </StyledGridContainer>
        <Divider variant="middle" />
        <StyledGridContainer item container wrap="wrap">
          <Grid item component="h4">
            Note History
          </Grid>
          <NotesPanel notes={notes} isLoading={isSpotNotesFetching} />
        </StyledGridContainer>
        <StyledGridContainer container spacing={2}>
          <Grid item xs={9}>
            <StyledTextField
              label="Add note..."
              variant="outlined"
              fullWidth={true}
              multiline={true}
              value={note}
              onChange={handleNoteTextFieldChange}
              inputProps={{
                maxLength: 240,
                "data-testid": "spot-leg-note-text-field",
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <BaseButton
              onClick={handleSubmittingNote}
              fullWidth={true}
              style={{ marginTop: "6px" }}
              disabled={!note || isAddingSpotNote || isSpotLegDetailsFetching}
              startIcon={
                isAddingSpotNote ? <CircularProgress size={15} /> : undefined
              }
              data-testid="spot-leg-note-submit-btn"
            >
              SUBMIT
            </BaseButton>
          </Grid>
        </StyledGridContainer>
      </div>
    </>
  );
}

CarrierSpotLegDetails.propTypes = {
  handleNotification: PropTypes.func.isRequired,
};

export default CarrierSpotLegDetails;
